import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  SparkStructureDto,
  SparkTreeNodeDto,
} from '@/app/common/general-types/spark-ads-v3.dto';
import { SparkAdsStore } from '../../../../services/spark-ads-store';
import { SparkLoadState } from '../../../../spark-ads.types';
import {
  formatCurrency,
  formatMultiplier,
  formatPercent,
  formatShortNumber,
} from '../../../../spark-ads.format';
import {
  FlatTreeRow,
  expandableNodeIds,
  flattenTree,
  levelName,
  levelTag,
  levelTagClass,
  statusFor,
} from './campaign-structure-tree.helpers';

/**
 * Campaign › ad group › Spark ad, with each level's own metrics.
 *
 * The three depths share one grid template so the metric columns line up regardless
 * of how deep a row sits.
 */
@Component({
  selector: 'sa-campaign-structure-tree',
  imports: [],
  templateUrl: './campaign-structure-tree.html',
  styleUrl: './campaign-structure-tree.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignStructureTree {
  readonly structure = input<SparkStructureDto | undefined>(undefined);
  readonly loadState = input.required<SparkLoadState>();

  protected readonly store = inject(SparkAdsStore);

  protected readonly rows = computed<FlatTreeRow[]>(() =>
    flattenTree(this.structure()?.nodes ?? [], (nodeId) => this.store.isTreeRowOpen(nodeId)),
  );

  protected readonly isEmpty = computed(
    () => this.loadState() === 'loaded' && (this.structure()?.nodes.length ?? 0) === 0,
  );

  private readonly currencyCode = computed(() => this.structure()?.currencyCode ?? null);

  protected toggle(row: FlatTreeRow): void {
    if (row.hasChildren) {
      this.store.toggleTreeRow(row.node.id);
    }
  }

  protected expandAll(): void {
    this.store.expandTreeRows(expandableNodeIds(this.structure()?.nodes ?? []));
  }

  protected collapseAll(): void {
    this.store.collapseTreeRows();
  }

  protected readonly hasExpandableRows = computed(
    () => expandableNodeIds(this.structure()?.nodes ?? []).length > 0,
  );

  // --- Row presentation -----------------------------------------------------

  protected tagFor(node: SparkTreeNodeDto): string {
    return levelTag(node.level);
  }

  protected tagClassFor(node: SparkTreeNodeDto): string {
    return levelTagClass(node.level);
  }

  protected levelNameFor(node: SparkTreeNodeDto): string {
    return levelName(node.level);
  }

  protected statusLabelFor(node: SparkTreeNodeDto): string {
    return statusFor(node).label;
  }

  protected statusPillClassFor(node: SparkTreeNodeDto): string {
    return statusFor(node).pillClass;
  }

  /** Depth tint, mirroring the design's progressively lighter rows. */
  protected rowClassFor(row: FlatTreeRow): string {
    const base = row.hasChildren ? 'cursor-pointer' : 'cursor-default';
    const tint =
      row.depth === 0
        ? 'bg-card'
        : row.depth === 1
          ? 'bg-muted/40'
          : 'bg-muted/70';
    return `${base} ${tint}`;
  }

  protected nameClassFor(row: FlatTreeRow): string {
    if (row.depth === 0) {
      return 'text-sm font-bold text-primary';
    }
    if (row.depth === 1) {
      return 'text-sm font-semibold text-primary';
    }
    return 'text-sm font-medium text-secondary';
  }

  protected indentFor(row: FlatTreeRow): string {
    return `${row.depth * 16}px`;
  }

  // --- Metric formatting ----------------------------------------------------

  protected spendFor(node: SparkTreeNodeDto): string {
    return formatCurrency(node.metrics.spend, this.currencyCode(), 0);
  }

  protected impressionsFor(node: SparkTreeNodeDto): string {
    return formatShortNumber(node.metrics.impressions);
  }

  protected ctrFor(node: SparkTreeNodeDto): string {
    return formatPercent(node.metrics.ctr);
  }

  protected conversionsFor(node: SparkTreeNodeDto): string {
    return formatShortNumber(node.metrics.conversions);
  }

  protected roasFor(node: SparkTreeNodeDto): string {
    return formatMultiplier(node.metrics.roas);
  }
}
